import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface FreelancerOption {
  id: string;
  nome: string;
}

interface FreelancerAsyncComboboxProps {
  value: FreelancerOption | null;
  onChange: (value: FreelancerOption | null) => void;
  className?: string;
}

/**
 * Picker assíncrono de freelancer para o filtro da lista de vagas. Busca
 * server-side por nome via RPC search_freelancers (só nome, barato) com debounce;
 * a filtragem NÃO é do cmdk (shouldFilter=false), quem filtra é o banco.
 */
export function FreelancerAsyncCombobox({
  value,
  onChange,
  className,
}: FreelancerAsyncComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["vagas-list-freelancer-picker", debounced],
    enabled: open && debounced.length >= 2,
    staleTime: 60_000,
    queryFn: async (): Promise<FreelancerOption[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("search_freelancers", {
        p_search: debounced,
        p_search_only_name: true,
        p_limit: 20,
        p_offset: 0,
      });
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((row) => ({ id: row.id, nome: row.nome }));
    },
  });

  const handleSelect = (option: FreelancerOption) => {
    onChange(option);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-white font-normal"
          >
            <span className="truncate">
              {value ? value.nome : "Filtrar por freelancer"}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar freelancer..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isFetching && (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Buscando...
                </div>
              )}
              {!isFetching && debounced.length < 2 && (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  Digite ao menos 2 letras
                </div>
              )}
              {!isFetching && debounced.length >= 2 && (
                <CommandEmpty>Nenhum freelancer encontrado.</CommandEmpty>
              )}
              {!isFetching && options.length > 0 && (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      onSelect={() => handleSelect(option)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value?.id === option.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{option.nome}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => onChange(null)}
          title="Limpar freelancer"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
